import { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import type { ForecastResult } from '../types/wasm';

interface ForecastChartProps {
  historicalData: Array<{ date: string; value: number }>;
  forecastResult: ForecastResult;
  forecastDates: string[];
}

export function ForecastChart({ historicalData, forecastResult, forecastDates }: ForecastChartProps) {
  const [isChartReady, setIsChartReady] = useState(false);

  // Show chart after data is computed
  useEffect(() => {
    setIsChartReady(false);
    const timer = setTimeout(() => {
      setIsChartReady(true);
    }, 50);
    return () => clearTimeout(timer);
  }, [forecastResult, forecastDates]);

  // Combine historical and forecast data for visualization
  const chartData = [
    // Historical data with fitted values
    ...historicalData.map((point, index) => ({
      date: point.date,
      actual: point.value,
      fitted: forecastResult.fitted_values[index],
      isForecast: false,
    })),
    // Forecast data
    ...forecastDates.map((date, index) => ({
      date,
      forecast: forecastResult.forecasts[index],
      lower: forecastResult.lower_bounds[index],
      upper: forecastResult.upper_bounds[index],
      isForecast: true,
    })),
  ];

  // Custom tooltip to show all values
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
          <p className="font-semibold text-gray-900">{data.date}</p>
          {data.actual !== undefined && (
            <p className="text-blue-600">Actual: {data.actual.toFixed(2)}</p>
          )}
          {data.fitted !== undefined && (
            <p className="text-green-600">Fitted: {data.fitted.toFixed(2)}</p>
          )}
          {data.forecast !== undefined && (
            <>
              <p className="text-purple-600 font-semibold">Forecast: {data.forecast.toFixed(2)}</p>
              <p className="text-gray-600 text-sm">
                CI: [{data.lower.toFixed(2)}, {data.upper.toFixed(2)}]
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Forecast Visualization</h2>

      {!isChartReady ? (
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
            <p className="text-sm text-gray-600">Rendering chart...</p>
          </div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ paddingTop: '20px' }} />

          {/* Confidence interval area */}
          <Area
            type="monotone"
            dataKey="upper"
            stroke="none"
            fill="#c084fc"
            fillOpacity={0.2}
            name="Upper Bound"
          />
          <Area
            type="monotone"
            dataKey="lower"
            stroke="none"
            fill="#c084fc"
            fillOpacity={0.2}
            name="Lower Bound"
          />

          {/* Actual values line */}
          <Line
            type="monotone"
            dataKey="actual"
            stroke="#2563eb"
            strokeWidth={2}
            dot={{ r: 3 }}
            name="Actual"
            connectNulls
          />

          {/* Fitted values line */}
          <Line
            type="monotone"
            dataKey="fitted"
            stroke="#10b981"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            name="Fitted"
            connectNulls
          />

          {/* Forecast line */}
          <Line
            type="monotone"
            dataKey="forecast"
            stroke="#9333ea"
            strokeWidth={2}
            dot={{ r: 4 }}
            name="Forecast"
            connectNulls
          />
        </ComposedChart>
      </ResponsiveContainer>
      )}

      <div className="mt-6 grid grid-cols-3 gap-4 text-center">
        <div className="p-3 bg-blue-50 rounded-lg">
          <div className="text-sm text-gray-600">Historical Data</div>
          <div className="text-2xl font-bold text-blue-600">{historicalData.length}</div>
          <div className="text-xs text-gray-500">data points</div>
        </div>
        <div className="p-3 bg-purple-50 rounded-lg">
          <div className="text-sm text-gray-600">Forecast Horizon</div>
          <div className="text-2xl font-bold text-purple-600">{forecastDates.length}</div>
          <div className="text-xs text-gray-500">periods ahead</div>
        </div>
        <div className="p-3 bg-green-50 rounded-lg">
          <div className="text-sm text-gray-600">Execution Time</div>
          <div className="text-2xl font-bold text-green-600">
            {forecastResult.execution_time_ms.toFixed(0)}
          </div>
          <div className="text-xs text-gray-500">milliseconds</div>
        </div>
      </div>
    </div>
  );
}
